/* import __COLOCATED_TEMPLATE__ from './trending-topic-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import md5 from 'blueimp-md5';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import { VISUALIZATION_TYPES } from 'embercom/components/reporting/custom/chart-builder/filter-bar/chart-type-filter';
import { insightsMetrics } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import moment from 'moment-timezone';
import Range from 'embercom/models/reporting/range';
import type { LogicalFilter } from 'embercom/components/reporting/custom/filters';

type FeedbackValue = 'positive' | 'negative';

type FeedbackLocalStorage = {
  [key: string]: FeedbackValue;
};

const FEEDBACK_SURVEY_ID = 50178727;
const LOCAL_STORAGE_KEY = 'trending-topic-card-feedback';

interface Args {
  trendingTopic: TrendingTopic;
  onShowConversationExplorer: (topic: TrendingTopic) => void;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
}

export default class TrendingTopicCard extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;

  @trackedInLocalStorage({
    keyName: LOCAL_STORAGE_KEY,
    defaultValue: '{}',
  })
  feedbackStorage = '{}';

  get appId() {
    return this.appService.app.id;
  }

  get adminId() {
    return this.appService.app.currentAdmin.id;
  }

  get topicName() {
    return this.args.trendingTopic.topicName;
  }

  get subtopicName() {
    return this.args.trendingTopic.subtopicName;
  }

  get submittedFeedbackObject(): FeedbackLocalStorage {
    try {
      return JSON.parse(this.feedbackStorage) as FeedbackLocalStorage;
    } catch {
      return {};
    }
  }

  get hasProvidedFeedback() {
    return !!this.submittedFeedbackObject[this.feedbackLocalStorageKey];
  }

  get feedbackLocalStorageKey() {
    let weekStart = this.args.trendingTopic.weekStart;
    return md5(`${this.appId}-${this.adminId}-${this.topicName}-${this.subtopicName}-${weekStart}`);
  }

  get statsDateRange() {
    // We don't use the app's timezone here, the date range is determined by an ML-pipeline, which uses UTC time
    let weekStartMoment = moment.utc(this.args.trendingTopic.weekStart);
    let weekEndMoment = weekStartMoment.clone().add(6, 'days');
    return Range.createFromParams(weekStartMoment.format(), weekEndMoment.format(), 'UTC');
  }

  get statsFilters(): LogicalFilter {
    return {
      type: 'and',
      filters: [
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_topic',
            property: 'conversation_custom_fields#ai_insights_topic',
            values: [this.topicName],
          },
        },
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_subtopic',
            property: 'conversation_custom_fields#ai_insights_subtopic',
            values: [this.subtopicName],
          },
        },
      ],
    };
  }

  get stats() {
    return [
      {
        id: 'volume',
        label: insightsMetrics(this.intl).volume.label,
        chartDefinition: this.buildChartDefinition(insightsMetrics(this.intl).volume.metric_id),
      },
      {
        id: 'resolution_rate',
        label: insightsMetrics(this.intl).resolutionRate.label,
        chartDefinition: this.buildChartDefinition(
          insightsMetrics(this.intl).resolutionRate.metric_id,
        ),
      },
      {
        id: 'involvement_rate',
        label: insightsMetrics(this.intl).involvementRate.label,
        chartDefinition: this.buildChartDefinition(
          insightsMetrics(this.intl).involvementRate.metric_id,
        ),
      },
      {
        id: 'ai_csat_score',
        label: insightsMetrics(this.intl).aiCsatScore.label,
        chartDefinition: this.buildChartDefinition(
          insightsMetrics(this.intl).aiCsatScore.metric_id,
        ),
      },
    ];
  }

  buildChartDefinition(metricId: string) {
    return {
      id: `${metricId}-${this.topicName}-${this.subtopicName}`,
      view_by: null,
      visualization_type: VISUALIZATION_TYPES.COUNTER,
      view_by_time_interval: 'day',
      visualization_options: {
        show_time_comparison: metricId === this.args.trendingTopic.primaryMetric,
      },
      chart_series: [{ metric_id: metricId }],
    };
  }

  @action
  onFeedback(feedback: FeedbackValue) {
    this.args.trackAnalyticsEvent('feedback_submitted', 'trending_topic_card', {
      topic: this.topicName,
      subtopic: this.subtopicName,
      week_start: this.args.trendingTopic.weekStart,
      feedback,
    });

    startSurvey(FEEDBACK_SURVEY_ID);

    let submittedFeedbackCopy = this.submittedFeedbackObject;
    submittedFeedbackCopy[this.feedbackLocalStorageKey] = feedback;
    this.feedbackStorage = JSON.stringify(submittedFeedbackCopy);
  }

  @action
  onShowConversationExplorer(topic: TrendingTopic) {
    this.args.onShowConversationExplorer(topic);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends::TrendingTopicCard': typeof TrendingTopicCard;
  }
}
